import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { Users, TrendingUp, AlertCircle, CheckCircle2, Calendar, RefreshCw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ManagerDashboard() {
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/manager/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento dashboard");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
    </div>
  );

  if (!data) return null;

  const totalClients = data.agents.reduce((s, a) => s + a.n_clients, 0);
  const totalRevenue = data.agents.reduce((s, a) => s + a.revenue_month, 0);
  const totalOrders = data.agents.reduce((s, a) => s + a.orders_month, 0);
  const totalOverdue = data.agents.reduce((s, a) => s + a.overdue, 0);

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#0F172A]"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          Dashboard Area Manager
        </h1>
        <p className="text-slate-500 text-sm mt-0.5">
          Distretto: <span className="font-semibold text-[#0F172A]">{data.district?.name || "—"}</span>
          &nbsp;·&nbsp; {format(new Date(), "EEEE d MMMM yyyy", { locale: it })}
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Users, label: "Clienti totali", value: totalClients, color: "text-blue-600", bg: "bg-blue-50" },
          { icon: TrendingUp, label: "Fatturato mese", value: `€${totalRevenue.toLocaleString("it-IT")}`, color: "text-emerald-600", bg: "bg-emerald-50" },
          { icon: CheckCircle2, label: "Ordini mese", value: totalOrders, color: "text-violet-600", bg: "bg-violet-50" },
          { icon: AlertCircle, label: "Visite scadute", value: totalOverdue, color: "text-red-600", bg: "bg-red-50" },
        ].map(({ icon: Icon, label, value, color, bg }) => (
          <div key={label} className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2">
            <div className={`w-9 h-9 ${bg} rounded-xl flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${color}`} />
            </div>
            <div className="text-xl font-bold text-[#0F172A]">{value}</div>
            <div className="text-xs text-slate-500">{label}</div>
          </div>
        ))}
      </div>

      {/* Agents table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="font-bold text-[#0F172A]">Agenti del distretto</h2>
          <p className="text-xs text-slate-400 mt-0.5">{data.total_agents} agente{data.total_agents !== 1 ? "i" : ""} attivi</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                <th className="text-left px-5 py-3">Agente</th>
                <th className="text-center px-4 py-3">Clienti</th>
                <th className="text-center px-4 py-3">Visite oggi</th>
                <th className="text-center px-4 py-3">Completate</th>
                <th className="text-center px-4 py-3">Scadute</th>
                <th className="text-center px-4 py-3">Ordini/mese</th>
                <th className="text-right px-5 py-3">Fatturato/mese</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.agents.map((agent) => (
                <tr key={agent.user_id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="font-semibold text-[#0F172A]">{agent.name}</div>
                    <div className="text-xs text-slate-400">{agent.sigla || agent.email}</div>
                  </td>
                  <td className="px-4 py-3.5 text-center font-medium">{agent.n_clients}</td>
                  <td className="px-4 py-3.5 text-center text-slate-600">
                    {agent.today_pending + agent.today_completed}
                  </td>
                  <td className="px-4 py-3.5 text-center">
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${agent.today_completed > 0 ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
                      {agent.today_completed}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-center">
                    {agent.overdue > 0 ? (
                      <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-50 text-red-600">
                        {agent.overdue}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5 text-center text-violet-700 font-medium">{agent.orders_month}</td>
                  <td className="px-5 py-3.5 text-right font-bold text-emerald-600">
                    €{(agent.revenue_month || 0).toLocaleString("it-IT")}
                  </td>
                </tr>
              ))}
              {data.agents.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-8 text-center text-slate-400 text-sm">
                    Nessun agente nel distretto
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
